import {
  addSetting,
  copySetting,
  removeSettingByIndex,
  setSettingByIndex,
} from "@/features/commissionSlice";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import _ from "lodash";
import moment from "moment";
import React from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

const MemoTransferAmountFormRow = React.memo(
  function TransferAmountFormRow({
    index,
    item,
    validations,
    disabled,
    promoterOptions,
    removeFormData,
    copyFormData,
    currentDate,
    handleFormData,
  }) {
    return (
      <Stack key={index} sx={{ my: 2 }} direction={"row"} spacing={2}>
        <Stack
          direction={"row"}
          justifyContent="space-between"
          alignItems={"flex-start"}
          sx={{ flex: 7 }}
          spacing={1}
        >
          <DatePicker
            sx={{ flex: 1 }}
            disabled={disabled}
            id="date"
            name="date"
            label="日期"
            value={item.date}
            minDate={moment(currentDate).startOf("month").format("YYYY-MM-DD")}
            maxDate={moment(currentDate).endOf("month").format("YYYY-MM-DD")}
            onChange={(value) => {
              handleFormData(
                index,
                "date",
                moment(value).format("YYYY-MM-DD"),
                item,
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                error={!!validations[index]?.dateErr}
                helperText={validations[index]?.dateErr}
              />
            )}
          ></DatePicker>
          <Autocomplete
            sx={{ flex: 1 }}
            required
            disabled={disabled}
            id="combo-box-demo"
            options={promoterOptions}
            value={item.transferUser}
            onChange={async (e, newValue) => {
              handleFormData(index, "transferUser", newValue, item);
            }}
            label="員工"
            renderInput={(params) => <TextField {...params} label="員工" />}
          />
          <TextField
            disabled={true}
            value={item.rosterString}
            id="outlined-basic"
            label="更表"
            variant="outlined"
          />
          <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <TextField
              disabled={disabled}
              error={!!validations[index]?.transferAmountErr}
              helperText={validations[index]?.transferAmountErr}
              sx={{ maxWidth: 380, minWidth: 100 }}
              value={item.transferAmount}
              type="number"
              id="outlined-basic"
              label="調整金額"
              variant="outlined"
              onChange={(event) =>
                handleFormData(
                  index,
                  "transferAmount",
                  event.target.value,
                  item,
                )
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />
          </Box>
        </Stack>
        {!disabled && (
          <Stack
            direction={"row"}
            justifyContent="space-between"
            alignItems={"flex-start"}
            sx={{ flex: 1 }}
          >
            <IconButton onClick={() => copyFormData(item, index)}>
              <ContentCopyIcon></ContentCopyIcon>
            </IconButton>
            <IconButton color="error" onClick={() => removeFormData(index)}>
              <DeleteForeverIcon></DeleteForeverIcon>
            </IconButton>
          </Stack>
        )}
      </Stack>
    );
  },
  (prev, next) => {
    return (
      next.index === prev.index &&
      _.isEqual(prev.item, next.item) &&
      _.isEqual(prev.validations, next.validations)
    );
  },
);

export default function TransferAmountFormRows({
  disabled,
  promoterOptions,
  productOptions,
  handleRoster,
  productCategories,
}) {
  const dispatch = useDispatch();

  const transferAmounts = useSelector(
    (state) => state.commission.settings.transferAmounts,
    shallowEqual,
  );

  const currentDate = useSelector(
    (state) => state.commission.settings.currentDate,
  );

  const validations = useSelector(
    (state) => state.commission.validations.transferAmounts,
    shallowEqual,
  );
  const handleRosterRef = React.useRef(handleRoster);
  handleRosterRef.current = handleRoster;

  const removeFormData = React.useCallback(
    async (index) => {
      dispatch(
        removeSettingByIndex({
          key: "transferAmounts",
          index: index,
        }),
      );
    },
    [dispatch],
  );
  const copyFormData = React.useCallback(
    async (item, index) => {
      dispatch(
        copySetting({
          key: "transferAmounts",
          index: index,
          value: {
            date: item.date,
            transferAmount: -item.transferAmount,
            roster: "FULL",
            rosterString: "",
            key: item + 1,
          },
        }),
      );
    },
    [dispatch],
  );
  const handleFormData = React.useCallback(
    async (index, key, value, currentValue) => {
      let cloneRow = { ...currentValue };
      cloneRow[key] = value;
      if (
        cloneRow["transferUser"] &&
        cloneRow.date &&
        (key === "date" || key === "transferUser")
      ) {
        cloneRow = await handleRosterRef.current(
          cloneRow,
          cloneRow["transferUser"].value,
          cloneRow.date,
        );
      }
      dispatch(
        setSettingByIndex({
          key: "transferAmounts",
          value: cloneRow,
          index: index,
        }),
      );
    },
    [dispatch],
  );
  const createFormData = React.useCallback(() => {
    dispatch(
      addSetting({
        key: "transferAmounts",
        value: {
          date: moment(currentDate).format("YYYY-MM-DD"),
          transferQty: null,
          roster: "FULL",
          rosterString: "",
        },
      }),
    );
  }, [dispatch, currentDate]);
  return (
    <React.Fragment>
      {_.map(transferAmounts, (item, index) => (
        <MemoTransferAmountFormRow
          item={item}
          key={item.id}
          index={index}
          validations={validations}
          productCategories={productCategories}
          disabled={disabled}
          promoterOptions={promoterOptions}
          removeFormData={removeFormData}
          copyFormData={copyFormData}
          productOptions={productOptions}
          currentDate={currentDate}
          handleFormData={handleFormData}
        ></MemoTransferAmountFormRow>
      ))}
      {!disabled && (
        <Box>
          <IconButton component="label" onClick={createFormData}>
            <AddBoxOutlinedIcon />
          </IconButton>
        </Box>
      )}
    </React.Fragment>
  );
}
